import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Sheet,
  SheetContent,
  SheetClose,
  SheetTitle,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Shield,
  FileText,
  Package,
  Users,
  Stethoscope,
  CreditCard,
  Store,
  Settings,
  AlertCircle,
  Loader2,
  X,
  Activity,
  BarChart3,
  Calendar,
  FlaskConical,
  MessageSquare,
  ClipboardList,
  Webhook,
  Brain,
  ListChecks,
  TrendingUp,
  ShieldCheck,
} from 'lucide-react';
import { Role } from '@/pages/RoleManagement';
import { roleFormSchema, RoleFormData } from '@/validators/role.schema';
import { roleService, Module as ApiModule } from '@/services/roleService';

interface RoleFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  role: Role | null;
  onSave: (roleData: Partial<Role>) => void;
  isLoading: boolean;
}

// Module display config — icons + human-readable names. Falls back gracefully for
// modules not in this map (uses the raw catalog name + a generic Shield icon).
const MODULE_DISPLAY: Record<string, { name: string; icon: typeof FileText }> = {
  prescriptions: { name: 'Orders', icon: FileText },
  products: { name: 'Products', icon: Package },
  patients: { name: 'Patients', icon: Users },
  clinical_notes: { name: 'Clinical Notes', icon: Stethoscope },
  practitioners: { name: 'Practitioners', icon: Users },
  payments: { name: 'Payments', icon: CreditCard },
  storefront: { name: 'Storefront', icon: Store },
  clinic_settings: { name: 'Clinic Settings', icon: Settings },
  // New (Phase C) modules
  roles: { name: 'Roles', icon: ShieldCheck },
  users: { name: 'Users', icon: Users },
  webhooks: { name: 'Webhooks', icon: Webhook },
  commissions: { name: 'Commissions', icon: TrendingUp },
  billing_config: { name: 'Billing Config', icon: CreditCard },
  programs: { name: 'Programs', icon: Package },
  subscriptions: { name: 'Subscriptions', icon: ListChecks },
  intellihealth: { name: 'IntelliHealth', icon: Brain },
  appointments: { name: 'Appointments', icon: Calendar },
  labs: { name: 'Labs', icon: FlaskConical },
  reports: { name: 'Reports', icon: BarChart3 },
  messaging: { name: 'Messaging', icon: MessageSquare },
  intakes: { name: 'Intakes', icon: ClipboardList },
  tasks: { name: 'Tasks', icon: Activity },
  partner_failures: { name: 'Integration Issues', icon: AlertCircle },
};

const PERMISSION_LABELS: Record<string, { label: string; description: string }> = {
  view: { label: 'View', description: 'Read-only access' },
  create: { label: 'Create', description: 'Create new records' },
  update: { label: 'Update', description: 'Edit existing records' },
  delete: { label: 'Delete', description: 'Soft-delete records' },
  manage: { label: 'Manage', description: 'Full management access' },
  collect_payment: { label: 'Collect Payment', description: 'Process payments' },
};

type PermissionModule = {
  id: string;
  name: string;
  icon: typeof FileText;
  actions: Array<{ id: string; label: string; description: string }>;
};

const toPermissionModules = (apiModules: ApiModule[]): PermissionModule[] =>
  apiModules
    .map(m => {
      const display = MODULE_DISPLAY[m.name] || { name: m.name, icon: Shield };
      return {
        id: m.name,
        name: display.name,
        icon: display.icon,
        actions: (m.permissions || []).map(p => ({
          id: p.name,
          label: PERMISSION_LABELS[p.name]?.label || p.name,
          description: PERMISSION_LABELS[p.name]?.description || p.name,
        })),
      };
    })
    // Stable display order: prefer modules we have a display config for, then alphabetical.
    .sort((a, b) => {
      const aHasDisplay = MODULE_DISPLAY[a.id] ? 0 : 1;
      const bHasDisplay = MODULE_DISPLAY[b.id] ? 0 : 1;
      if (aHasDisplay !== bHasDisplay) return aHasDisplay - bHasDisplay;
      return a.name.localeCompare(b.name);
    });

const RoleFormDialog: React.FC<RoleFormDialogProps> = ({ open, onOpenChange, role, onSave, isLoading }) => {
  const [permissionModules, setPermissionModules] = useState<PermissionModule[]>([]);
  const [modulesLoading, setModulesLoading] = useState(false);

  // Pull the modules + permissions catalog from the API so newly seeded modules
  // (e.g. webhooks, commissions, billing_config) appear without an FE redeploy.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setModulesLoading(true);
    roleService
      .getClinicModules()
      .then(res => {
        if (cancelled) return;
        setPermissionModules(toPermissionModules(res.data?.modules || []));
      })
      .catch(() => {
        if (cancelled) return;
        setPermissionModules([]);
      })
      .finally(() => {
        if (!cancelled) setModulesLoading(false);
      });
    return () => { cancelled = true; };
  }, [open]);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm<RoleFormData>({
    resolver: zodResolver(roleFormSchema),
    defaultValues: {
      name: '',
      description: '',
      permissions: {},
    },
  });

  const permissions = watch('permissions');

  useEffect(() => {
    if (role) {
      reset({
        name: role.name,
        description: role.description,
        permissions: role.permissions,
      });
    } else {
      reset({
        name: '',
        description: '',
        permissions: {},
      });
    }
  }, [role, open, reset]);

  const handlePermissionChange = (moduleId: string, actionId: string, checked: boolean) => {
    const currentPermissions = { ...permissions };
    const modulePermissions = currentPermissions[moduleId] || [];
    
    if (checked) {
      currentPermissions[moduleId] = [...modulePermissions, actionId];
    } else {
      const filtered = modulePermissions.filter(id => id !== actionId);
      if (filtered.length === 0) {
        delete currentPermissions[moduleId];
      } else {
        currentPermissions[moduleId] = filtered;
      }
    }
    
    setValue('permissions', currentPermissions);
  };

  const isPermissionChecked = (moduleId: string, actionId: string) => {
    return permissions?.[moduleId]?.includes(actionId) || false;
  };

  const onSubmit = (data: RoleFormData) => {
    onSave(data);
    reset();
  };

  const getPermissionCount = () => {
    return Object.values(permissions || {}).reduce((sum, actions) => sum + actions.length, 0);
  };

  const handleSelectAll = (moduleId: string, actions: { id: string }[]) => {
    const currentPermissions = { ...permissions };
    const allActionIds = actions.map(a => a.id);
    const modulePermissions = currentPermissions[moduleId] || [];
    const allSelected = allActionIds.every(id => modulePermissions.includes(id));

    if (allSelected) {
      delete currentPermissions[moduleId];
    } else {
      currentPermissions[moduleId] = allActionIds;
    }
    setValue('permissions', currentPermissions);
  };

  const isAllSelected = (moduleId: string, actions: { id: string }[]) => {
    const modulePermissions = permissions?.[moduleId] || [];
    return actions.every(a => modulePermissions.includes(a.id));
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent 
        className="w-full !max-w-[640px] rounded-l-2xl p-0 flex flex-col gap-0 [&>button]:hidden"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <SheetTitle className="font-semibold text-slate-900" style={{ fontSize: '1.05rem' }}>
            {role ? 'Edit Role' : 'Create Role'}
          </SheetTitle>
          <SheetClose asChild>
            <button className="w-10 h-10 rounded-full border border-slate-200 flex items-center justify-center hover:bg-slate-50 transition-colors">
              <X className="h-5 w-5 text-slate-600" />
            </button>
          </SheetClose>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-6">
          <form id="role-form" onSubmit={handleSubmit(onSubmit)} className="space-y-8">
            {/* Basic Information */}
            <div className="space-y-4">
              <h3 className="uppercase text-[0.8rem] font-semibold text-[#60378B]">Basic Information</h3>
              
              <div className="space-y-4">
                {/* Role Name */}
                <div className="space-y-1.5">
                  <Label htmlFor="name" className="text-sm font-medium text-slate-700">
                    Role Name <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="name"
                    placeholder="e.g., Receptionist, Nurse, Manager"
                    {...register('name')}
                    onChange={(e) => setValue('name', e.target.value.trimStart(), { shouldValidate: true })}
                    className={errors.name ? 'border-red-500' : ''}
                  />
                  {errors.name && (
                    <p className="text-xs text-red-600 flex items-center gap-1">
                      <AlertCircle className="h-3 w-3" />
                      {errors.name.message}
                    </p>
                  )}
                </div>

                {/* Description */}
                <div className="space-y-1.5">
                  <Label htmlFor="description" className="text-sm font-medium text-slate-700">
                    Description <span className="text-red-500">*</span>
                  </Label>
                  <Textarea
                    id="description"
                    placeholder="Brief description of this role's responsibilities..."
                    {...register('description')}
                    onChange={(e) => setValue('description', e.target.value.trimStart(), { shouldValidate: true })}
                    className={`min-h-[80px] resize-none ${errors.description ? 'border-red-500' : ''}`}
                    rows={3}
                  />
                  {errors.description && (
                    <p className="text-xs text-red-600 flex items-center gap-1">
                      <AlertCircle className="h-3 w-3" />
                      {errors.description.message}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Module Permissions */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="uppercase text-[0.8rem] font-semibold text-[#60378B]">Module Permissions</h3>
                <span className="text-xs text-slate-500">
                  {getPermissionCount()} permission{getPermissionCount() !== 1 ? 's' : ''} selected
                </span>
              </div>

              {/* Warning if no permissions */}
              {getPermissionCount() === 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2">
                  <AlertCircle className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                  <p className="text-xs text-amber-800">
                    No permissions selected. Users with this role won't be able to access any modules.
                  </p>
                </div>
              )}

              {/* Permissions List */}
              <div className="space-y-3">
                {modulesLoading && (
                  <div className="flex items-center justify-center py-6 text-sm text-slate-500">
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Loading modules…
                  </div>
                )}
                {!modulesLoading && permissionModules.length === 0 && (
                  <div className="text-sm text-slate-500 py-6 text-center">
                    No permission modules available.
                  </div>
                )}
                {permissionModules.map((module) => {
                  const Icon = module.icon;
                  const hasAnyPermission = module.actions.some(action =>
                    isPermissionChecked(module.id, action.id)
                  );

                  return (
                    <div
                      key={module.id}
                      className={`border rounded-lg transition-colors ${
                        hasAnyPermission ? 'border-brand-teal bg-brand-teal/5' : 'border-slate-200'
                      }`}
                    >
                      {/* Module Header */}
                      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center">
                            <Icon className="h-4 w-4 text-slate-600" />
                          </div>
                          <span className="font-medium text-slate-900 text-sm">{module.name}</span>
                        </div>
                        <button
                          type="button"
                          onClick={() => handleSelectAll(module.id, module.actions)}
                          className="text-xs text-brand-teal hover:underline"
                        >
                          {isAllSelected(module.id, module.actions) ? 'Deselect all' : 'Select all'}
                        </button>
                      </div>

                      {/* Actions */}
                      <div className="px-4 py-3 space-y-4">
                        {module.actions.map(action => (
                          <label
                            key={action.id}
                            htmlFor={`${module.id}-${action.id}`}
                            className="flex items-start gap-3 cursor-pointer"
                          >
                            <Checkbox
                              id={`${module.id}-${action.id}`}
                              checked={isPermissionChecked(module.id, action.id)}
                              onCheckedChange={checked =>
                                handlePermissionChange(module.id, action.id, checked as boolean)
                              }
                              className="mt-[2px]"
                            />
                            <div className="flex-1 min-w-0">
                              <span className="font-medium text-slate-700 text-sm block leading-none">
                                {action.label}
                              </span>
                              <span className="text-xs text-slate-500 block mt-0.5">
                                {action.description}
                              </span>
                            </div>
                          </label>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </form>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-slate-200 bg-white">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            form="role-form"
            className="bg-brand-teal hover:bg-brand-teal/90"
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                {role ? 'Updating...' : 'Creating...'}
              </>
            ) : (
              <>
                <Shield className="h-4 w-4" />
                {role ? 'Update Role' : 'Create Role'}
              </>
            )}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default RoleFormDialog;
